import React, { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  CloudProviderContext,
  shouldShowCloudProviderToggle,
  getImplicitCloudProviderWhenToggleHidden,
} from "../../../CloudProviderContext";
import { AwsLogoMark, AzureLogoMark } from "../../Common/CloudProviderMark";
import { useAccounts } from "../../Common/HelperComponents/GetDataFromStore";

const CLOUD_PROVIDERS = [
  { id: "AWS", label: "AWS", Logo: AwsLogoMark },
  { id: "AZURE", label: "Azure", Logo: AzureLogoMark },
];

const CloudProviderToggle = () => {
  const { selectedProvider, setSelectedProvider } = useContext(CloudProviderContext);
  const accounts = useAccounts();
  const accountsLoading = useSelector((state) => state.orgAccount.loading);

  // Same pool as /accounts API, minus init/test accounts
  const accountsForToggle = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts.filter(
      (account) =>
        !account.accountNumber?.includes("INIT00000001") &&
        !account.accountNumber?.includes("TEST")
    );
  }, [accounts]);

  const showToggle = useMemo(
    () => shouldShowCloudProviderToggle(accountsForToggle),
    [accountsForToggle]
  );

  const implicitProvider = useMemo(
    () => getImplicitCloudProviderWhenToggleHidden(accountsForToggle),
    [accountsForToggle]
  );

  // When toggle is hidden, drive view from the single explicit type (or AWS default)
  useEffect(() => {
    if (accountsLoading || showToggle) return;
    if (implicitProvider !== selectedProvider) {
      setSelectedProvider(implicitProvider);
    }
  }, [
    accountsLoading,
    showToggle,
    implicitProvider,
    selectedProvider,
    setSelectedProvider,
  ]);

  if (accountsLoading || !showToggle) {
    return null;
  }

  return (
    <div
      className="header-cloud-toggle d-none d-lg-inline-flex"
      role="group"
      aria-label="Cloud provider"
    >
      {CLOUD_PROVIDERS.map((provider) => {
        const isActive = selectedProvider === provider.id;
        const Logo = provider.Logo;

        return (
          <button
            key={provider.id}
            type="button"
            className={`header-cloud-toggle-btn${isActive ? " active" : ""}`}
            data-provider={provider.id}
            onClick={() => setSelectedProvider(provider.id)}
            aria-pressed={isActive}
            aria-label={provider.label}
            title={provider.label}
          >
            <Logo
              className={`header-cloud-toggle-logo header-cloud-toggle-logo--${provider.id === "AWS" ? "aws" : "azure"}`}
            />
          </button>
        );
      })}
    </div>
  );
};

export default CloudProviderToggle;
